// Tested in the use-active-circle tests
import {
    createHasPermission,
    createGlobalClassifierIdentifiers,
} from '@calm/evexia-web-common/dist/circle';
import useAccount from '@rhg/hooks/use-account';
import { useTracking } from '@rhg/libs/analytics';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { CircleJwtContext } from '@rhg/providers/circle-jwt';
import { useContext, useEffect, useMemo } from 'react';

export function useGetCircle() {
    const { account } = useAccount();
    const { getAccessTokenSilently } = useAuth();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (circleId: number) => {
            const api = createCircleApi(getAccessTokenSilently);
            const response = await api.getUsersCirclesRecipientV1({
                userId: account!.userId!,
                recipientId: circleId,
            });
            const circle = response.data;
            queryClient.setQueryData(
                [queryKeys.circleById, circle.recipient.id],
                circle
            );
            return circle;
        },
    });
    return { getCircle: mutate, getCircleAsync: mutateAsync, ...otherProps };
}

export function useCircle(circleId: number) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { setUserProfile } = useTracking();
    const { account } = useAccount();

    const enabled = !!(isAuthenticated && account?.userId && circleId > 0);
    const { data, refetch, ...otherProps } = useQuery({
        queryKey: [queryKeys.circleById, circleId],
        queryFn: async () => {
            const api = createCircleApi(getAccessTokenSilently);
            const response = await api.getUsersCirclesRecipientV1({
                userId: account!.userId!,
                recipientId: circleId,
            });
            const partnerId = response?.data?.sponsor?.partnerId;
            const classifierIdentifiers = createGlobalClassifierIdentifiers(
                response.data
            );
            setUserProfile({
                ...(partnerId ? { PartnerID: partnerId } : {}),
                UserSponsoredCountry:
                    classifierIdentifiers.countryCodeClassifier?.toUpperCase(),
                IsDependent: response.data.sponsor?.isDependentGroup,
            });
            return response.data;
        },
        enabled,
        retry: false,
    });

    const hasPermission = useMemo(
        () => createHasPermission(data?.cohortInfo?.permissionDetail?.perms),
        [data]
    );

    const globalClassifierIdentifiers = useMemo(
        () => createGlobalClassifierIdentifiers(data),
        [data]
    );

    const { setAuthorizationJWT, authorizationJWTs } =
        useContext(CircleJwtContext);

    useEffect(() => {
        setAuthorizationJWT(circleId, data?.authorizationJWT, () =>
            refetch({ cancelRefetch: false })
        );
    }, [circleId, data?.authorizationJWT, refetch, setAuthorizationJWT]);

    const circle: typeof data = useMemo(
        () =>
            data
                ? {
                      ...data,
                      ...(Object.keys(authorizationJWTs).includes(
                          circleId.toString()
                      )
                          ? { authorizationJWT: authorizationJWTs[circleId] }
                          : {}),
                  }
                : data,
        [data, authorizationJWTs, circleId]
    );

    return {
        circle,
        /**
         * Returns true if the user has permission to perform the operation to the data class.
         * @param dataClass The name of the data class. `keyof CirclePermissionMap`
         * @param requiredAccess The required permission OR array of permissions
         * @example
         * ```tsx
         * export function SomeComponent() {
         *   const { activeCircle, activeCircleHasPermission } = useActiveCircle()
         *   return (
         *     <div>
         *       {activeCircleHasPermission('avatar', 'c') && <button>Upload Avatar</button>}
         *       {activeCircleHasPermission('inbox', 'c') && <button>Compose Message</button>}
         *     </div>
         *   )
         * }
         * ```
         */
        globalClassifierIdentifiers,
        hasPermission,
        isEnabled: enabled,
        refetch,
        ...otherProps,
    };
}
